<template>
  <div :class="['tr', `tr--${tone}`]">
    <div class="tr__head">
      <i :class="['feather', icon, 'tr__icon']"></i>
      <div>
        <div class="tr__verdict">{{ result.message }}</div>
        <div v-if="result.item && result.item.name" class="tr__item">{{ result.item.name }}</div>
      </div>
    </div>

    <!-- O nome é o que o validador confere; por isso vem grande. -->
    <div v-if="holderName" class="tr__holder">
      <div class="tr__holder-name">{{ holderName }}</div>
      <div v-if="holderDocument" class="tr__holder-doc">CPF {{ holderDocument }}</div>
    </div>

    <dl v-if="occurrence" class="tr__facts">
      <div v-if="occurrence.date">
        <dt>Sessão</dt>
        <dd>{{ formattedDate }}<span v-if="occurrence.start_time"> · {{ occurrence.start_time }}</span></dd>
      </div>
      <div v-if="occurrence.origin_label">
        <dt>Origem</dt>
        <dd>{{ occurrence.origin_label }}</dd>
      </div>
      <div v-if="result.ticket && result.ticket.code">
        <dt>Código</dt>
        <dd>{{ result.ticket.code }}</dd>
      </div>
    </dl>

    <!-- Entrada anterior: é a informação que resolve a discussão na porta. -->
    <p v-if="showPreviousEntry" class="tr__note">
      Entrada registrada às {{ (result.check_in.at || '').slice(11, 16) }}
      <template v-if="result.check_in.validated_by && result.check_in.validated_by.name">
        por {{ result.check_in.validated_by.name }}
      </template>.
    </p>
  </div>
</template>

<script>
/**
 * Veredito de uma validação, desenhado para ser lido de relance.
 *
 * A cor vem de `result.success`, e não do código: um dispositivo pode passar a
 * devolver um resultado novo (a lista é aditiva por contrato) e o desconhecido
 * precisa cair em "não entra", nunca em verde.
 */
export default {
  name: 'TicketResult',
  props: {
    result: {
      type: Object,
      required: true
    }
  },
  computed: {
    tone() {
      if (this.result.success) return 'ok'

      return this.result.result === 'ALREADY_CHECKED_IN' ? 'warn' : 'bad'
    },
    icon() {
      return {
        ok: 'icon-check-circle',
        warn: 'icon-alert-triangle',
        bad: 'icon-x-circle'
      }[this.tone]
    },
    holderName() {
      return (this.result.holder && this.result.holder.name) || ''
    },
    holderDocument() {
      return (this.result.holder && this.result.holder.document_masked) || ''
    },
    occurrence() {
      return this.result.occurrence || null
    },
    formattedDate() {
      const value = this.occurrence && this.occurrence.date
      if (!value) return ''

      const parts = String(value).split('-')

      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value
    },
    showPreviousEntry() {
      return this.result.result === 'ALREADY_CHECKED_IN' && this.result.check_in
    }
  }
}
</script>

<style lang="scss" scoped>
.tr {
  padding: 1.1rem;
  border-radius: 0.6rem;
  border-left: 5px solid transparent;

  &--ok {
    background: rgba(40, 199, 111, 0.1);
    border-left-color: #28c76f;
  }

  &--warn {
    background: rgba(255, 159, 67, 0.12);
    border-left-color: #ff9f43;
  }

  &--bad {
    background: rgba(234, 84, 85, 0.1);
    border-left-color: #ea5455;
  }
}

.tr__head {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
}

.tr__icon {
  font-size: 1.6rem;
  line-height: 1.2;

  .tr--ok & {
    color: #28c76f;
  }

  .tr--warn & {
    color: #ff9f43;
  }

  .tr--bad & {
    color: #ea5455;
  }
}

.tr__verdict {
  font-size: 1.05rem;
  font-weight: 600;
}

.tr__item {
  color: #6e6b7b;
  font-size: 0.82rem;
}

.tr__holder {
  margin-top: 0.9rem;
}

.tr__holder-name {
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.2;
}

.tr__holder-doc {
  color: #6e6b7b;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}

.tr__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin: 0.9rem 0 0;

  dt {
    color: #6e6b7b;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  dd {
    margin: 0.1rem 0 0;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
}

.tr__note {
  margin: 0.8rem 0 0;
  color: #6e6b7b;
  font-size: 0.82rem;
}
</style>
